import { useEffect, useState } from "react";
import { PointOfSaleTransaction } from "../../../model/pointofsale/PointOfSaleTransaction";
import dayjs from "dayjs";
import { Card, Skeleton, Table, Button, message, Space, Tag, Row, Col, Statistic } from "antd";
import { PrinterOutlined, EditOutlined, CaretRightOutlined, CaretDownOutlined } from "@ant-design/icons";
import { usePointOfSaleService } from "../../../services/pointofsale/PointOfSaleServiceProvider";
import { useReceiptPrinterService } from "../../../services/printing/ReceiptPrinterServiceProvider";
import { ReceiptPrinterService } from "../../../services/printing/ReceiptPrinterService";
import UpdateTransactionModal from "../../pointofsale/UpdateTransactionModal";
import type { SortOrder } from 'antd/es/table/interface';
import { useFormatCurrency, useCurrencySymbol } from '../../../hooks/useFormatters';

interface PointOfSaleTransactionViewProps {
    memberId: string;
    memberName: string;
    backgroundColor?: string;
    borderColor?: string;
    // When false, the transactions are view-only: the Edit action is disabled
    // (e.g. a member viewing their own profile). Defaults to true.
    canEdit?: boolean;
}

interface TransactionItem {
    key: string;
    type: string;
    itemName: string;
    quantity: number;
    unitPrice: number;
    amount: number;
    facilityName?: string;
    isBooking: boolean;
    startTime?: Date;
    endTime?: Date;
}

const PointOfSaleTransactionView: React.FC<PointOfSaleTransactionViewProps> = ({
    memberId,
    memberName,
    backgroundColor = '#ffffff',
    borderColor = '#e5e7eb',
    canEdit = true
}) => {
    const [loading, setLoading] = useState(true);
    const [pointOfSaleTransactions, setPointOfSaleTransactions] = useState<PointOfSaleTransaction[]>([]);
    const [updateTransactionModalVisible, setUpdateTransactionModalVisible] = useState(false);
    const [selectedTransaction, setSelectedTransaction] = useState<PointOfSaleTransaction | null>(null);
    const [expandedRows, setExpandedRows] = useState<string[]>([]);
    const pointOfSaleService = usePointOfSaleService();
    const receiptPrinterService = useReceiptPrinterService();
    const formatCurrency = useFormatCurrency();
    const currencySymbol = useCurrencySymbol();

    useEffect(() => {
        if (memberId) { 
            const fetchPointOfSaleTransactions = async () => {
                const transactions = await pointOfSaleService.getPointOfSaleTransactions(memberId);
                // The API client resolves undefined on failure — keep the empty list.
                setPointOfSaleTransactions(Array.isArray(transactions) ? transactions : []);
                setLoading(false);
            }
            fetchPointOfSaleTransactions();
        }
    }, [memberId, pointOfSaleService]);

    const handleTransactionUpdated = () => {
        // Refresh the transactions after update
        if (memberId) {
            const fetchPointOfSaleTransactions = async () => {
                const transactions = await pointOfSaleService.getPointOfSaleTransactions(memberId);
                setPointOfSaleTransactions(Array.isArray(transactions) ? transactions : []);
            }
            fetchPointOfSaleTransactions();
        }
    };

    // Generate transaction items for display
    const generateTransactionItems = (transaction: PointOfSaleTransaction): TransactionItem[] => {
        const items: TransactionItem[] = [];

        // Add regular items
        transaction.items.forEach((item, itemIndex) => {
            items.push({
                key: `item-${itemIndex}`,
                type: item.item.type,
                itemName: item.item.name,
                quantity: item.quantity,
                unitPrice: item.item.price,
                amount: item.item.price * item.quantity,
                isBooking: false
            });
        });

        // Add facility bookings
        transaction.bookingSlots.forEach((slot, slotIndex) => {
            items.push({
                key: `booking-${slotIndex}`,
                type: 'FacilityBookings',
                itemName: `${slot.bookingSlot.facilityName} Booking`,
                quantity: 1,
                unitPrice: slot.price,
                amount: slot.price,
                facilityName: slot.bookingSlot.facilityName,
                isBooking: true,
                startTime: slot.bookingSlot.startTime,
                endTime: slot.bookingSlot.endTime
            });
        });

        return items;
    };

    // Calculate summary statistics
    const summary = {
        totalTransactions: pointOfSaleTransactions.length,
        totalAmount: pointOfSaleTransactions.reduce((sum, t) => sum + t.amount, 0),
        completedAmount: pointOfSaleTransactions
            .filter(t => t.status === 'Completed')
            .reduce((sum, t) => sum + t.amount, 0),
        unpaidAmount: pointOfSaleTransactions
            .filter(t => t.status !== 'Completed')
            .filter(t => t.status !== 'Cancelled')
            .reduce((sum, t) => sum + t.amount, 0),
        completedCount: pointOfSaleTransactions.filter(t => t.status === 'Completed').length,
        unpaidCount: pointOfSaleTransactions.filter(t => t.status !== 'Completed' && t.status !== 'Cancelled').length
    };

    if (loading) {
        return <Skeleton active />;
    }

    function calculateStatusColor(status: string): string {
        if (status === 'Completed') {
            return 'green';
        }
        if (status === 'Cancelled') {
            return 'grey';
        }
        if (status === 'Pending') {
            return 'orange';
        }

        if (status === 'Unpaid') {
            return 'red';
        }
        return 'white';
    }

    const columns = [
        {
            title: 'Date',
            dataIndex: 'timestamp',
            key: 'timestamp',
            render: (timestamp: Date) => dayjs(timestamp).format('DD/MM/YYYY HH:mm'),
            sorter: (a: PointOfSaleTransaction, b: PointOfSaleTransaction) => 
                dayjs(a.timestamp).unix() - dayjs(b.timestamp).unix(),
            defaultSortOrder: 'descend' as SortOrder,
            width: 150
        },
        {
            title: 'Amount',
            dataIndex: 'amount',
            key: 'amount',
            render: (amount: number) => formatCurrency(amount),
            sorter: (a: PointOfSaleTransaction, b: PointOfSaleTransaction) => a.amount - b.amount,
            width: 120,
            align: 'right' as const
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (status: string) => (
                <Tag color={calculateStatusColor(status)}>
                    {status}
                </Tag>
            ),
            sorter: (a: PointOfSaleTransaction, b: PointOfSaleTransaction) => 
                a.status.localeCompare(b.status),
            width: 100
        },
        {
            title: 'Items',
            key: 'items',
            render: (text: string, record: PointOfSaleTransaction) => {
                const items = generateTransactionItems(record);
                return (
                    <div>
                        <div style={{ fontWeight: '500' }}>
                            {items.length} item{items.length !== 1 ? 's' : ''}
                        </div>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                            {items.map(item => {
                                if (item.isBooking && item.startTime && item.endTime) {
                                    return `${item.facilityName} (${dayjs(item.startTime).format('dddd Do MMMM YYYY')} ${dayjs(item.startTime).format('HH:mm')}-${dayjs(item.endTime).format('HH:mm')})`;
                                }
                                return item.itemName;
                            }).join(', ')}
                        </div>
                    </div>
                );
            },
            width: 250
        },
        {
            title: 'Actions',
            key: 'actions',
            render: (text: string, record: PointOfSaleTransaction) => (
                <Space>
                    <Button
                        type="text"
                        icon={<EditOutlined />}
                        disabled={!canEdit}
                        onClick={() => {
                            setSelectedTransaction(record);
                            setUpdateTransactionModalVisible(true);
                        }}
                    >
                        Edit
                    </Button>
                    <Button 
                        type="text" 
                        icon={<PrinterOutlined />}
                        onClick={async () => {
                            try {
                                const receiptData = ReceiptPrinterService.createReceiptDataFromTransaction(record, memberName);
                                const printSuccess = await receiptPrinterService.printReceipt(receiptData, false);
                                if (printSuccess) {
                                    message.success('Receipt printed successfully!');
                                } else {
                                    message.error('Failed to print receipt.');
                                }
                            } catch (error) {
                                console.error('Error printing receipt:', error);
                                message.error('Failed to print receipt.');
                            }
                        }}
                    >
                        Print
                    </Button>
                </Space>
            ),
            width: 150
        }
    ];

    // Expandable row content
    const expandedRowRender = (record: PointOfSaleTransaction) => {
        const items = generateTransactionItems(record);
        
        const itemColumns = [
            {
                title: 'Type',
                dataIndex: 'type',
                key: 'type',
                width: 120,
                render: (type: string) => (
                    <Tag color={type === 'FacilityBookings' ? 'blue' : 'default'}>
                        {type}
                    </Tag>
                )
            },
            {
                title: 'Item/Service',
                dataIndex: 'itemName',
                key: 'itemName',
                width: 250,
                render: (text: string, record: TransactionItem) => (
                    <div>
                        <div>{record.itemName}</div>
                        {record.isBooking && record.startTime && record.endTime && (
                            <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                                {dayjs(record.startTime).format('dddd Do MMMM YYYY')} {dayjs(record.startTime).format('HH:mm')} → {dayjs(record.endTime).format('HH:mm')}
                            </div>
                        )}
                    </div>
                )
            },
            {
                title: 'Qty',
                dataIndex: 'quantity',
                key: 'quantity',
                width: 80,
                align: 'right' as const
            },
            {
                title: 'Unit Price',
                dataIndex: 'unitPrice',
                key: 'unitPrice',
                width: 120,
                align: 'right' as const,
                render: (value: number) => formatCurrency(value)
            },
            {
                title: 'Amount',
                dataIndex: 'amount',
                key: 'amount',
                width: 120,
                align: 'right' as const,
                render: (value: number) => formatCurrency(value)
            }
        ];

        return (
            <div style={{ padding: '16px', backgroundColor: '#fafafa' }}>
                <div style={{ marginBottom: '16px' }}>
                    <h4 style={{ margin: 0, color: '#666' }}>Transaction Details</h4>
                </div>
                <Table
                    dataSource={items}
                    columns={itemColumns}
                    pagination={false}
                    size="small"
                    rowKey="key"
                    scroll={{ x: 800 }}
                />
            </div>
        );
    };

    return (
        <>
            {/* Summary Statistics */}
            <Card 
                title="Transaction Summary"
                className="mb-4"
                style={{ 
                    backgroundColor,
                    borderColor
                }}
            >
                <Row gutter={[16, 16]}>
                    <Col xs={12} sm={8} md={8} lg={4}>
                        <Statistic
                            title="Total Transactions"
                            value={summary.totalTransactions} 
                        />
                    </Col>
                    <Col xs={12} sm={8} md={8} lg={4}>
                        <Statistic
                            title="Total Amount" 
                            value={summary.totalAmount} 
                            precision={2}
                            prefix={currencySymbol}
                        />
                    </Col>
                    <Col xs={12} sm={8} md={8} lg={4}>
                        <Statistic
                            title="Completed" 
                            value={summary.completedCount}
                            suffix={`/ ${formatCurrency(summary.completedAmount)}`}
                            valueStyle={{ color: '#3f8600' }}
                        />
                    </Col>
                    <Col xs={12} sm={8} md={8} lg={4}>
                        <Statistic
                            title="Unpaid" 
                            value={summary.unpaidCount}
                            suffix={`/ ${formatCurrency(summary.unpaidAmount)}`}
                            valueStyle={{ color: '#cf1322' }}
                        />
                    </Col>
                    <Col xs={12} sm={8} md={8} lg={4}>
                        <Statistic
                            title="Payment Rate" 
                            value={summary.totalAmount > 0 ? (summary.completedAmount / summary.totalAmount * 100) : 0}
                            precision={1}
                            suffix="%"
                            valueStyle={{ 
                                color: summary.totalAmount > 0 && (summary.completedAmount / summary.totalAmount) >= 0.8 ? '#3f8600' : '#cf1322' 
                            }}
                        />
                    </Col>
                </Row>
            </Card>

            {/* Transactions Table */}
            <Card 
                title="Point of Sale Transactions"
                className="mb-4"
                style={{ 
                    backgroundColor,
                    borderColor
                }}
            >
                <Table
                    columns={columns}
                    dataSource={pointOfSaleTransactions}
                    pagination={{ pageSize: 5 }}
                    rowKey={(record) => `${record.timestamp}-${record.amount}`}
                    rowClassName={(record) => record.status !== 'Completed' ? 'highlight-row' : ''}
                    expandable={{
                        expandedRowRender,
                        expandedRowKeys: expandedRows,
                        onExpandedRowsChange: (expandedRows) => {
                            setExpandedRows(expandedRows.map(key => key.toString()));
                        },
                        expandIcon: ({ expanded, onExpand, record }) => (
                            <Button
                                type="text"
                                icon={expanded ? <CaretDownOutlined /> : <CaretRightOutlined />}
                                onClick={(e) => onExpand(record, e)}
                                style={{ padding: '4px' }}
                            />
                        )
                    }}
                    scroll={{ x: 800 }}
                />
            </Card>

            {/* Update Transaction Modal */}
            {updateTransactionModalVisible && selectedTransaction && (
                <UpdateTransactionModal
                    visible={updateTransactionModalVisible}
                    transaction={selectedTransaction}
                    onCancel={() => setUpdateTransactionModalVisible(false)}
                    onSuccess={() => {
                        setUpdateTransactionModalVisible(false);
                        handleTransactionUpdated(); // Refresh the data
                    }}
                />
            )}
        </>
    );
};

export default PointOfSaleTransactionView;


